import { useMount, useMemoizedFn } from 'ahooks';
import { useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { getFormatDate, ORDER_DATE_FORMAT } from 'components/RevxDate';
import { RevxMoney } from 'components/RevxMoney';
import { UnderlineText } from 'components/RevxTypography';
import {
  JobAdjustmentStatusEnum,
  JobAdjustmentViewer,
  JobItem,
} from 'domains/workspaceDomain';
import JobAdjustmentModal from '../../../../components/JobAdjustmentModal';
import './index.less';

interface Props {
  jobData: JobItem;
}

const CommonJobAdjustmentLink = (props: Props) => {
  const { jobData } = props;
  const dispatch = useAppDispatch();
  const [jobAdjustmentData, setJobAdjustmentData] =
    useState<JobAdjustmentViewer[]>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const latestAdjustment = jobAdjustmentData?.[jobAdjustmentData.length - 1];
  const effectiveStatus =
    (latestAdjustment?.processStatus as unknown as JobAdjustmentStatusEnum) ||
    jobData.adjustmentStatus;

  const loadAdjustmentData = useMemoizedFn(async () => {
    if (
      [
        JobAdjustmentStatusEnum.ACCEPTED,
        JobAdjustmentStatusEnum.EDITING,
        JobAdjustmentStatusEnum.AWAITING_APPROVAL,
        JobAdjustmentStatusEnum.AWAITING_ACCEPT,
      ].includes(jobData.adjustmentStatus)
    ) {
      const res = await dispatch.rfm.jobsAdjustmentsAll(`${jobData.id}`);
      setJobAdjustmentData(res);
    }
  });

  useMount(() => {
    loadAdjustmentData();
  });

  const handleModalSuccess = async () => {
    await loadAdjustmentData();
    dispatch.workspace.reloadJobList();
  };

  const handleOpenModal = () => {
    setModalVisible(true);
  };

  return (
    <>
      <div>
        <div className={`job-adjustment total-job-cost`}>
          <label className="footer-left">
            {`JOB ADJUSTMENT - ${getFormatDate({
              dateStr:
                jobAdjustmentData?.[jobAdjustmentData.length - 1]?.createAt ||
                '',
              formatStr: ORDER_DATE_FORMAT,
              isLocalTime: true,
            })}`}
          </label>

          <div className="footer-right">
            <p>
              {[
                JobAdjustmentStatusEnum.EDITING,
                JobAdjustmentStatusEnum.AWAITING_APPROVAL,
                JobAdjustmentStatusEnum.AWAITING_ACCEPT,
              ].includes(effectiveStatus) ? (
                <>
                  <label>NEW TOTAL:</label>
                  <span className="job-adjustment-money">TBD</span>
                </>
              ) : (
                <>
                  <label>NEW TOTAL:</label>
                  <span className="job-adjustment-money">
                    <RevxMoney
                      value={latestAdjustment?.adjustTotal?.totalCharge || 0}
                    />
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        <div
          className={`summary-card-container`}
          style={{
            padding: '20px 20px 20px 100px',
          }}
        >
          <UnderlineText color="green" onClick={handleOpenModal}>
            {(() => {
              const adjustmentDate = latestAdjustment?.createAt
                ? ` - ${getFormatDate({
                    dateStr: latestAdjustment.createAt,
                    formatStr: ORDER_DATE_FORMAT,
                    isLocalTime: true,
                  })}`
                : '';

              if (
                [
                  JobAdjustmentStatusEnum.EDITING,
                  JobAdjustmentStatusEnum.AWAITING_APPROVAL,
                  JobAdjustmentStatusEnum.AWAITING_ACCEPT,
                ].includes(effectiveStatus)
              ) {
                return `Job Adjustment In Progress${adjustmentDate}`;
              }
              if (
                [JobAdjustmentStatusEnum.ACCEPTED].includes(effectiveStatus)
              ) {
                return `Approved Job Adjustment${adjustmentDate}`;
              }
              if (
                [JobAdjustmentStatusEnum.DECLINED].includes(effectiveStatus)
              ) {
                return `Declined Job Adjustment${adjustmentDate}`;
              }
              return null;
            })()}
          </UnderlineText>
        </div>
      </div>
      <JobAdjustmentModal
        visible={modalVisible}
        jobId={jobData.id}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
      />
    </>
  );
};

export default CommonJobAdjustmentLink;
